/**
 * Simple slot picker. Works with 12/24h formats.
 *
 * This component is controlled through the slot prop, and uses the onChange
 * callback in props to let its parent know of any change.
 */

import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import Select from './shared/Select';

type SlotPickerPropType = {
  shouldUse24hTime: boolean;
  onChange: (timestamp: number) => void;
  slots: Array<{
    isSlotFree: boolean;
    timestamp: number;
  }>;
  slot: Dayjs;
  emptyByDefault?: boolean;
  timezone?: string;
  metaData: {
    updateId: string;
    scheduledAt: string;
  };
};

const SlotTimePicker = styled.div`
  margin-top: 16px;
`;

const SlotPicker = ({
  metaData,
  shouldUse24hTime,
  onChange,
  slots,
  slot,
  timezone,
  emptyByDefault,
}: SlotPickerPropType) => {
  const [selectedSlot, setSelectedSlot] = useState(null);

  const EMPTY_OPTION_VALUE = 'empty';
  const slotTimestamp = slot.unix();
  const currentTimestampSeconds = Math.floor(Date.now() / 1000);
  const isSlotTimeInFuture = slotTimestamp > currentTimestampSeconds;
  const hasSlots = slots.length > 0;

  // Returns true if we're editing an existing update and the timestamp passed
  // matches the original scheduled time of that update (i.e. the scheduled time
  // before any changes were made to it during this MC session)
  const isOriginalTimeOfEditedUpdate = (timestamp) => {
    const isEditingUpdate = metaData && metaData.updateId !== null;
    return isEditingUpdate && timestamp === metaData.scheduledAt;
  };

  const slotsWithDisabledInfo = slots.map(({ timestamp, isSlotFree }) => {
    const isTimeInFuture = timestamp > currentTimestampSeconds;
    const isDisabled =
      (!isSlotFree || !isTimeInFuture) &&
      !isOriginalTimeOfEditedUpdate(timestamp);

    return { timestamp, isSlotFree, isDisabled };
  });

  const areAllOptionsDisabled = slotsWithDisabledInfo.every(
    ({ isDisabled }) => isDisabled
  );

  const isSlotTimestampAvailable =
    isOriginalTimeOfEditedUpdate(slotTimestamp) ||
    (isSlotTimeInFuture &&
      slots.some(
        ({ timestamp, isSlotFree }) => timestamp === slotTimestamp && isSlotFree
      ));
  const initialSelectedSlot =
    isSlotTimestampAvailable && !emptyByDefault
      ? slotTimestamp
      : EMPTY_OPTION_VALUE;

  const onSlotChange = (e) => {
    const selectedSlotTimestamp = e.target.value;
    setSelectedSlot(selectedSlotTimestamp);
    onChange(selectedSlotTimestamp);
  };

  const getHumanReadableTimeSlotLabel = (timestamp, isSlotFree) => {
    let slotMoment = dayjs.unix(timestamp);
    if (timezone) slotMoment = slotMoment.tz(timezone);

    const humanReadableFormat = shouldUse24hTime ? 'H:mm' : 'h:mm A';
    const humanReadableTime = slotMoment.format(humanReadableFormat);

    const isTimeInFuture = slotMoment.isAfter(new Date());
    const slotPastLabel = isTimeInFuture ? '' : ' (past)';
    const slotOccupancyLabel = isSlotFree
      ? ''
      : isOriginalTimeOfEditedUpdate(timestamp)
      ? ' (occupied by this post)'
      : ' (occupied)';

    return `${humanReadableTime}${slotOccupancyLabel || slotPastLabel}`;
  };

  return (
    <SlotTimePicker>
      {hasSlots && (
        <Select
          value={selectedSlot || initialSelectedSlot}
          onChange={onSlotChange}
        >
          <option disabled value={EMPTY_OPTION_VALUE}>
            {areAllOptionsDisabled
              ? 'Select a slot (all occupied)'
              : 'Select a slot'}
          </option>
          {slotsWithDisabledInfo.map(
            ({ timestamp, isSlotFree, isDisabled }) => (
              <option value={timestamp} disabled={isDisabled} key={timestamp}>
                {getHumanReadableTimeSlotLabel(timestamp, isSlotFree)}
              </option>
            )
          )}
        </Select>
      )}

      {!hasSlots && (
        <Select disabled value="">
          <option value="">No slots this day</option>
        </Select>
      )}
    </SlotTimePicker>
  );
};

SlotPicker.defaultProps = {
  metaData: undefined,
  emptyByDefault: false,
};

export default SlotPicker;
